/**
 * Zone color assignment: a fixed palette cycled over zones sorted by id, so
 * the plan tint and the legend stay in sync (same module, same order) and the
 * mapping is deterministic for identical inputs.
 */
import type { MepZone } from "../api/types";

/** Distinguishable tints that read against the dark plan background. */
export const ZONE_PALETTE = [
  "#4a90d9",
  "#98c379",
  "#e5c07b",
  "#c678dd",
  "#56b4d3",
  "#e0823c",
  "#d96ba8",
  "#7e6bd9",
] as const;

export function zoneColor(index: number): string {
  return ZONE_PALETTE[((index % ZONE_PALETTE.length) + ZONE_PALETTE.length) % ZONE_PALETTE.length];
}

export interface ZoneLegendRow {
  zone: MepZone;
  color: string;
}

/** Zones sorted by id with their assigned palette color (legend order). */
export function zoneLegend(zones: readonly MepZone[]): ZoneLegendRow[] {
  return [...zones]
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((zone, i) => ({ zone, color: zoneColor(i) }));
}

/** zone id → color, derived from the legend (kept in sync by construction). */
export function zoneColorById(zones: readonly MepZone[]): Map<string, string> {
  return new Map(zoneLegend(zones).map(({ zone, color }) => [zone.id, color]));
}

/** space id → zone tint color, for the plan view's zone fill. */
export function zoneTintBySpaceId(zones: readonly MepZone[]): Map<string, string> {
  const tints = new Map<string, string>();
  for (const { zone, color } of zoneLegend(zones)) {
    for (const spaceId of zone.space_ids) {
      if (!tints.has(spaceId)) tints.set(spaceId, color);
    }
  }
  return tints;
}
